// Keeps the native status bar of the Cordova apps in step with the app theme,
// in light and dark mode.
//
// util.toggleBootstrap53DarkMode paints the status bar with UIKit's named
// colours lightGray/darkGray whenever window.StatusBar exists, and sets the
// text style. Every caller of toggleBootstrap53DarkMode calls
// syncNativeStatusBar right after it so this module has the last word:
//
// - iOS: the WebView fills the screen (StatusBarOverlaysWebView=true in
//   mobileApp), so WebKit paints the document background (html/body in
//   custom.css, same colour as the page) behind the status bar. Only the text
//   style matters there; the colour call is a no-op.
// - Android: the colour call paints the status bar in the header colour on
//   devices that still honour it (.bg-lightmode #fff / .bg-darkmode #191d21).
//
// The hosted page loads cordova.js asynchronously (template-prod.html), so
// window.StatusBar usually does not exist yet when the app boots. Start
// watchNativeStatusBar once; it applies the colour as soon as the Cordova
// runtime reports deviceready.

export const NATIVE_STATUS_BAR_COLORS = Object.freeze({
    light: '#ffffff', // .bg-lightmode
    dark: '#191d21', // .bg-darkmode
});

const NATIVE_PLATFORMS = ['ios', 'android'];

function defaultDocument() {
    return typeof document !== 'undefined' ? document : null;
}

function defaultWindow() {
    return typeof window !== 'undefined' ? window : null;
}

export function isDarkThemeApplied(doc = defaultDocument()) {
    const root = doc?.documentElement;
    if (!root || typeof root.getAttribute !== 'function') {
        return false;
    }
    return root.getAttribute('data-bs-theme') === 'dark';
}

// The native wrapper (mobileApp/www/js/index.js) opens the hosted app with
// ?platform=ios|android; template-prod.html loads cordova.js only then.
export function isNativeAppPage(win = defaultWindow()) {
    const search = win?.location?.search;
    if (typeof search !== 'string' || !search) {
        return false;
    }
    const platform = new URLSearchParams(search).get('platform');
    return NATIVE_PLATFORMS.includes(platform);
}

export function syncNativeStatusBar({ darkmode, statusBar = defaultWindow()?.StatusBar } = {}) {
    if (!statusBar || typeof statusBar.backgroundColorByHexString !== 'function') {
        return false;
    }
    const dark = darkmode === undefined ? isDarkThemeApplied() : !!darkmode;
    try {
        statusBar.backgroundColorByHexString(dark ? NATIVE_STATUS_BAR_COLORS.dark : NATIVE_STATUS_BAR_COLORS.light);
        if (dark) {
            if (typeof statusBar.styleLightContent === 'function') {
                statusBar.styleLightContent();
            }
        } else if (typeof statusBar.styleDefault === 'function') {
            statusBar.styleDefault();
        }
        return true;
    } catch (err) {
        return false;
    }
}

// cordova.js replaces document.addEventListener so that a deviceready listener
// registered AFTER it loaded fires (immediately when the event already
// happened). A listener registered before cordova.js loaded never fires, hence
// the polling for window.cordova. Returns a function that stops the watcher.
export function watchNativeStatusBar({
    win = defaultWindow(),
    doc = defaultDocument(),
    interval = 250,
    maxAttempts = 120,
} = {}) {
    let attempts = 0;
    let timer = null;
    let stopped = false;

    const onDeviceReady = () => {
        if (stopped) {
            return;
        }
        syncNativeStatusBar({ darkmode: isDarkThemeApplied(doc), statusBar: win?.StatusBar });
    };

    const tick = () => {
        timer = null;
        if (stopped || !win || !doc) {
            return;
        }
        if (win.cordova) {
            doc.addEventListener('deviceready', onDeviceReady, false);
            return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
            return;
        }
        timer = setTimeout(tick, interval);
    };

    tick();

    return () => {
        stopped = true;
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        if (doc && typeof doc.removeEventListener === 'function') {
            doc.removeEventListener('deviceready', onDeviceReady, false);
        }
    };
}
